import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';
import SeoContent from '../components/SeoContent';
import { apiJumble } from '../utils/api';

const PUZZLES = [
  'GARDEN','PLANET','STREAM','CASTLE','WONDER','FOREST','WINTER','MASTER',
  'SILVER','TRAVEL','BRIDGE','DANGER','FINGER','MARKET','MOTHER','HUNTER',
  'SPRING','GRAVEL','FROZEN','AUTHOR','BANTER','CARTON','POSTER','BUNDLE',
  'CANDLE','MARBLE','PENCIL','GLANCE','THREAD','WEALTH','STRONG','CLOVER',
  'GATHER','OYSTER','TURNIP','PARCEL','FLOWER','PATROL','LAUNCH','INSULT',
  'SINGLE','LATHER','CORNER','GRAINS','STRIVE','MENTAL','SIMPLE','TRIBAL',
  'LOCKET','FLAKES',
];

const TIMER = 120;

function pts(len) {
  return len === 3 ? 100 : len === 4 ? 200 : len === 5 ? 400 : len >= 6 ? 800 : 0;
}

function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function countLetters(s) {
  const c = {};
  for (const l of s) c[l] = (c[l] || 0) + 1;
  return c;
}

function canForm(word, letterArr) {
  const avail = countLetters(letterArr.join(''));
  for (const l of word.toUpperCase()) {
    if (!avail[l]) return false;
    avail[l]--;
  }
  return true;
}

export default function WordTwist() {
  const [phase, setPhase]         = useState('idle');
  const [puzzle, setPuzzle]       = useState('');
  const [letters, setLetters]     = useState([]);
  const [input, setInput]         = useState('');
  const [possible, setPossible]   = useState([]);
  const [found, setFound]         = useState(new Set());
  const [score, setScore]         = useState(0);
  const [timeLeft, setTimeLeft]   = useState(TIMER);
  const [msg, setMsg]             = useState({ text: '', type: '' });
  const [shake, setShake]         = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const timerRef = useRef(null);
  const msgRef   = useRef(null);
  const inputRef = useRef(null);
  const usedRef  = useRef(new Set());

  const flash = useCallback((text, type = 'info') => {
    clearTimeout(msgRef.current);
    setMsg({ text, type });
    msgRef.current = setTimeout(() => setMsg({ text: '', type: '' }), 1800);
  }, []);

  const doShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  }, []);

  const startGame = useCallback(async () => {
    setPhase('loading');
    let pool = PUZZLES.filter(p => !usedRef.current.has(p));
    if (!pool.length) { usedRef.current.clear(); pool = PUZZLES; }
    const pw = pool[Math.floor(Math.random() * pool.length)];
    usedRef.current.add(pw);

    try {
      const { data } = await apiJumble(pw.toLowerCase());
      const words = (data.results || []).map(w => (w.word || w).toUpperCase());
      if (!words.includes(pw)) words.push(pw);

      setPuzzle(pw);
      setLetters(shuffleArr(pw.split('')));
      setPossible(words);
      setFound(new Set());
      setScore(0);
      setInput('');
      setTimeLeft(TIMER);
      setMsg({ text: '', type: '' });
      setPhase('playing');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch {
      flash('Failed to load puzzle. Check your connection.', 'error');
      setPhase('idle');
    }
  }, [flash]);

  useEffect(() => {
    if (phase !== 'playing') { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setPhase('gameover'); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const submit = useCallback(() => {
    const word = input.trim().toUpperCase();
    if (!word) return;

    if (word.length < 3) {
      flash('Need at least 3 letters', 'error'); doShake(); return;
    }
    if (!canForm(word, letters)) {
      flash('Use only the given letters!', 'error'); doShake(); setInput(''); return;
    }
    if (found.has(word)) {
      flash('Already found!', 'warn'); doShake(); setInput(''); return;
    }
    if (!possible.includes(word)) {
      flash('Not a valid word', 'error'); doShake(); setInput(''); return;
    }

    const p = pts(word.length);
    setFound(prev => new Set([...prev, word]));
    setScore(s => s + p);
    setInput('');

    if (word.length >= 6) {
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 1800);
      flash(`🎉 WORD TWIST! +${p} pts`, 'success');
    } else {
      flash(`+${p} pts`, 'success');
    }
    inputRef.current?.focus();
  }, [input, letters, found, possible, flash, doShake]);

  // Which tiles are "consumed" by the current input
  const tileUsed = useMemo(() => {
    const inputCounts = countLetters(input.toUpperCase());
    const consumed = {};
    return letters.map(l => {
      consumed[l] = consumed[l] || 0;
      const isUsed = consumed[l] < (inputCounts[l] || 0);
      if (isUsed) consumed[l]++;
      return isUsed;
    });
  }, [input, letters]);

  const clickTile = useCallback((l, i) => {
    if (!tileUsed[i]) {
      setInput(x => x + l);
    } else {
      const idx = input.toUpperCase().lastIndexOf(l);
      if (idx !== -1) setInput(x => x.slice(0, idx) + x.slice(idx + 1));
    }
    inputRef.current?.focus();
  }, [tileUsed, input]);

  const shuffleTiles = useCallback(() => {
    setLetters(l => shuffleArr(l));
    inputRef.current?.focus();
  }, []);

  const clearInput = useCallback(() => {
    setInput('');
    inputRef.current?.focus();
  }, []);

  // End-screen stats
  const foundArr = useMemo(() =>
    [...found].sort((a, b) => b.length - a.length || a.localeCompare(b)), [found]);

  const missedByLen = useMemo(() => {
    const missed = possible.filter(w => !found.has(w));
    const groups = {};
    for (const w of missed) {
      if (!groups[w.length]) groups[w.length] = [];
      groups[w.length].push(w);
    }
    return Object.entries(groups).sort(([a], [b]) => Number(b) - Number(a));
  }, [possible, found]);

  const pct   = possible.length > 0 ? Math.round((found.size / possible.length) * 100) : 0;
  const stars = pct >= 80 ? 4 : pct >= 50 ? 3 : pct >= 25 ? 2 : 1;
  const shareText = [
    `Word Twist 🔤`,
    `Score: ${score.toLocaleString()} pts`,
    `Found ${found.size}/${possible.length} words (${pct}%)`,
    '⭐'.repeat(stars),
    ``,
    `Play free at wordzingo.com/word-twist`,
  ].join('\n');

  const timerColor  = timeLeft > 60 ? '#16a34a' : timeLeft > 30 ? '#d97706' : '#dc2626';
  const timerPct    = (timeLeft / TIMER) * 100;
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const ss = String(timeLeft % 60).padStart(2, '0');

  return (
    <div className="game-page">
      <PageSEO page="word-twist" />

      {/* Celebration overlay */}
      {celebrate && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 900, pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.18)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '1.5rem 3rem',
            fontSize: '1.8rem', fontWeight: 900, fontFamily: 'Nunito, sans-serif',
            color: '#4f46e5', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            animation: 'wt-pop 0.4s ease',
          }}>
            🎉 WORD TWIST! 🎉
          </div>
        </div>
      )}

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 1rem' }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        {/* Header */}
        <div className="text-center mb-4">
          <h1 style={{
            fontFamily: 'Nunito, sans-serif', fontWeight: 900,
            fontSize: 'clamp(2rem, 6vw, 2.8rem)', color: '#1e1b4b', margin: 0,
          }}>
            🔤 Word Twist
          </h1>
          <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', margin: '0.3rem 0 0' }}>
            Build as many words as possible from 6 letters — you have 2 minutes!
          </p>
        </div>

        {/* ── IDLE ── */}
        {phase === 'idle' && (
          <div className="text-center">
            <div style={{
              background: '#fff', borderRadius: 20, padding: '2rem',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '2rem',
            }}>
              <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#1e1b4b', marginBottom: '1.5rem' }}>
                How to Play
              </h3>
              <div className="row g-3 text-start mb-4">
                {[
                  ['🔡', 'You get 6 scrambled letter tiles'],
                  ['⌨️', 'Type words or click the tiles to build one'],
                  ['✅', 'Any 3+ letter word using those letters scores'],
                  ['🎉', 'Find the 6-letter WORD TWIST for a big bonus!'],
                  ['🔀', 'Shuffle tiles anytime if you\'re stuck'],
                  ['⏱️', '2 minutes on the clock — how many can you find?'],
                ].map(([icon, text]) => (
                  <div key={text} className="col-12 col-sm-6">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                      <span style={{ fontFamily: 'Nunito, sans-serif', color: '#374151', fontSize: '0.95rem' }}>{text}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Scoring */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {[[3,'100 pts','#0891b2'],[4,'200 pts','#16a34a'],[5,'400 pts','#d97706'],[6,'800 pts 🎉','#4f46e5']].map(([len, p, col]) => (
                  <div key={len} style={{ background: '#f8fafc', borderRadius: 12, padding: '0.6rem 1rem', textAlign: 'center', border: `2px solid ${col}22` }}>
                    <div style={{ fontWeight: 900, color: col, fontSize: '1.15rem', fontFamily: 'Nunito, sans-serif' }}>{len}-letter</div>
                    <div style={{ color: '#6b7280', fontWeight: 700, fontSize: '0.82rem', fontFamily: 'Nunito, sans-serif' }}>{p}</div>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={startGame} style={{
              background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 50,
              padding: '0.9rem 3.5rem', fontWeight: 900, fontFamily: 'Nunito, sans-serif',
              fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 4px 18px rgba(79,70,229,0.35)',
              transition: 'transform 0.15s',
            }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              ▶ Start Game
            </button>
          </div>
        )}

        {/* ── LOADING ── */}
        {phase === 'loading' && (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#4f46e5', width: '3rem', height: '3rem' }} />
            <p style={{ marginTop: '1rem', fontFamily: 'Nunito, sans-serif', fontWeight: 700, color: '#6b7280' }}>
              Loading puzzle…
            </p>
          </div>
        )}

        {/* ── PLAYING ── */}
        {phase === 'playing' && (
          <div>
            {/* Score / Words / Timer row */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
              {[
                { label: 'Score', val: score.toLocaleString(), col: '#4f46e5' },
                { label: 'Words', val: found.size, col: '#0891b2' },
              ].map(({ label, val, col }) => (
                <div key={label} style={{
                  flex: 1, background: '#fff', borderRadius: 14, padding: '0.7rem 0.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.55rem', color: col, lineHeight: 1.1 }}>{val}</div>
                </div>
              ))}
              <div style={{
                flex: 1.6, background: '#fff', borderRadius: 14, padding: '0.7rem 0.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Time Left</div>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.55rem', color: timerColor, lineHeight: 1.1, transition: 'color 0.5s' }}>
                  {mm}:{ss}
                </div>
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden', marginTop: '0.25rem' }}>
                  <div style={{ height: '100%', width: `${timerPct}%`, background: timerColor, transition: 'width 1s linear, background 0.5s' }} />
                </div>
              </div>
            </div>

            {/* Message bar */}
            <div style={{
              height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '1rem',
              color: msg.type === 'success' ? '#16a34a' : msg.type === 'error' ? '#dc2626' : msg.type === 'warn' ? '#d97706' : 'transparent',
              transition: 'color 0.15s',
            }}>
              {msg.text || ' '}
            </div>

            {/* Letter tiles */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {letters.map((l, i) => (
                <div
                  key={i}
                  onClick={() => clickTile(l, i)}
                  style={{
                    width: 'clamp(46px, 10vw, 60px)', height: 'clamp(46px, 10vw, 60px)',
                    background: tileUsed[i] ? '#e2e8f0' : '#4f46e5',
                    color: tileUsed[i] ? '#94a3b8' : '#fff',
                    borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'clamp(1.3rem, 3.5vw, 1.8rem)',
                    fontWeight: 900, fontFamily: 'Nunito, sans-serif',
                    cursor: 'pointer',
                    boxShadow: tileUsed[i] ? 'none' : '0 4px 12px rgba(79,70,229,0.28)',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                    transform: tileUsed[i] ? 'scale(0.88)' : 'scale(1)',
                  }}
                >
                  {l}
                </div>
              ))}
            </div>

            {/* Input area */}
            <div style={{
              background: '#fff', borderRadius: 16, padding: '1rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '1rem',
              animation: shake ? 'wt-shake 0.4s' : 'none',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                borderBottom: `3px solid ${shake ? '#dc2626' : '#4f46e5'}`,
                paddingBottom: '0.5rem', marginBottom: '0.75rem',
                transition: 'border-color 0.2s',
              }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                    setInput(v.slice(0, 6));
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); submit(); }
                    if (e.key === ' ') { e.preventDefault(); shuffleTiles(); }
                  }}
                  placeholder="Type a word…"
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: 'clamp(1.4rem, 5vw, 1.9rem)', fontWeight: 900,
                    fontFamily: 'Nunito, sans-serif', color: '#1e1b4b',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                  }}
                  maxLength={6}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                <span style={{ fontFamily: 'Nunito, sans-serif', color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0 }}>
                  {input.length}/6
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={submit} disabled={input.length < 3}
                  style={{
                    flex: 1, minWidth: 80, borderRadius: 50, border: 'none',
                    padding: '0.6rem 1rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
                    fontSize: '0.95rem', cursor: input.length >= 3 ? 'pointer' : 'not-allowed',
                    background: input.length >= 3 ? '#4f46e5' : '#e2e8f0',
                    color: input.length >= 3 ? '#fff' : '#9ca3af', transition: 'all 0.15s',
                  }}>
                  ✓ Enter
                </button>
                <button onClick={shuffleTiles} style={{
                  borderRadius: 50, border: '2px solid #e2e8f0', padding: '0.6rem 1rem',
                  fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem',
                  background: '#fff', color: '#374151', cursor: 'pointer',
                }}>
                  🔀 Shuffle
                </button>
                <button onClick={clearInput} disabled={!input} style={{
                  borderRadius: 50, border: '2px solid #e2e8f0', padding: '0.6rem 1rem',
                  fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem',
                  background: '#fff', color: '#374151', cursor: 'pointer',
                  opacity: input ? 1 : 0.4,
                }}>
                  ✕ Clear
                </button>
              </div>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif' }}>
                Tip: press Space to shuffle tiles · Enter to submit
              </p>
            </div>

            {/* Found words */}
            {found.size > 0 && (
              <div style={{
                background: '#fff', borderRadius: 16, padding: '1rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#1e1b4b', marginBottom: '0.65rem', fontSize: '0.95rem' }}>
                  ✅ Found Words ({found.size})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {foundArr.map(w => (
                    <span key={w} style={{
                      background: w.length >= 6 ? '#4f46e5' : '#f0fdf4',
                      color: w.length >= 6 ? '#fff' : '#16a34a',
                      border: `1px solid ${w.length >= 6 ? '#4338ca' : '#bbf7d0'}`,
                      borderRadius: 8, padding: '0.2rem 0.65rem',
                      fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem',
                    }}>
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── GAME OVER ── */}
        {phase === 'gameover' && (
          <div>
            {/* Result card */}
            <div style={{
              background: '#fff', borderRadius: 20, padding: '2rem',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '1.25rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.8rem', marginBottom: '0.4rem' }}>⏰</div>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, color: '#1e1b4b', fontSize: '1.9rem', marginBottom: '0.2rem' }}>
                Time's Up!
              </h2>
              <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', marginBottom: '1.5rem' }}>
                The letters were: <strong style={{ letterSpacing: '0.2em', color: '#4f46e5' }}>{puzzle.split('').join(' ')}</strong>
              </p>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {[
                  { val: score.toLocaleString(), label: 'POINTS', col: '#4f46e5' },
                  { val: `${found.size}/${possible.length}`, label: 'WORDS', col: '#0891b2' },
                  { val: '⭐'.repeat(stars), label: 'RATING', col: '#d97706' },
                ].map(({ val, label, col }) => (
                  <div key={label} style={{ background: '#f8faff', borderRadius: 16, padding: '1rem 1.5rem', minWidth: 90 }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.7rem', color: col, lineHeight: 1.1 }}>{val}</div>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.72rem', color: '#9ca3af', fontWeight: 800, letterSpacing: '0.05em' }}>{label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={startGame} style={{
                  background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 50,
                  padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem', cursor: 'pointer', boxShadow: '0 3px 12px rgba(79,70,229,0.3)',
                }}>
                  🔄 Play Again
                </button>
                <button onClick={() => setShowShare(true)} style={{
                  background: '#fff', color: '#4f46e5', border: '2px solid #4f46e5', borderRadius: 50,
                  padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem', cursor: 'pointer',
                }}>
                  📤 Share Score
                </button>
              </div>
            </div>

            {/* Words found */}
            {found.size > 0 && (
              <div style={{
                background: '#fff', borderRadius: 16, padding: '1.25rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '1rem',
              }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#16a34a', marginBottom: '0.75rem' }}>
                  ✅ Words You Found ({found.size})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {foundArr.map(w => (
                    <span key={w} style={{
                      background: w.length >= 6 ? '#4f46e5' : '#f0fdf4',
                      color: w.length >= 6 ? '#fff' : '#16a34a',
                      border: `1px solid ${w.length >= 6 ? '#4338ca' : '#bbf7d0'}`,
                      borderRadius: 8, padding: '0.25rem 0.65rem',
                      fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem',
                    }}>
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Missed words grouped by length */}
            {missedByLen.length > 0 && (
              <div style={{
                background: '#fff', borderRadius: 16, padding: '1.25rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#dc2626', marginBottom: '1rem' }}>
                  🔍 Words You Missed
                </div>
                {missedByLen.map(([len, words]) => (
                  <div key={len} style={{ marginBottom: '0.85rem' }}>
                    <div style={{
                      fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#6b7280',
                      fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                      marginBottom: '0.35rem',
                    }}>
                      {len}-letter words
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {[...words].sort().map(w => (
                        <span key={w} style={{
                          background: Number(len) >= 6 ? '#fef3c7' : '#fef2f2',
                          color: Number(len) >= 6 ? '#92400e' : '#dc2626',
                          border: `1px solid ${Number(len) >= 6 ? '#fde68a' : '#fecaca'}`,
                          borderRadius: 8, padding: '0.2rem 0.6rem',
                          fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem',
                        }}>
                          {w}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showShare && (
          <ShareModal
            shareText={shareText}
            gameUrl="https://wordzingo.com/word-twist"
            onClose={() => setShowShare(false)}
            onNewGame={startGame}
          />
        )}
        <SeoContent game="word-twist" />
      </div>

      <style>{`
        @keyframes wt-shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)}
          60%{transform:translateX(-5px)}
          80%{transform:translateX(5px)}
        }
        @keyframes wt-pop {
          0%{transform:scale(0.7);opacity:0}
          60%{transform:scale(1.08)}
          100%{transform:scale(1);opacity:1}
        }
      `}</style>
    </div>
  );
}
